import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { alpha, styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile } from '@fortawesome/free-solid-svg-icons';

import { formatTime } from '../../../functions/Utils';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: 'hasSyllabus',
    numeric: false,
    disablePadding: false,
    label: '',
    sortingEnabled: false,
  },
  {
    id: 'course',
    numeric: false,
    disablePadding: false,
    label: 'Course',
    sortingEnabled: true,
  },
  {
    id: 'description',
    numeric: false,
    disablePadding: false,
    label: 'Title',
    sortingEnabled: true,
  },
  {
    id: 'classNumber',
    numeric: false,
    disablePadding: false,
    label: 'Class #',
    sortingEnabled: true,
  },
  {
    id: 'days',
    numeric: false,
    disablePadding: false,
    label: 'Days',
    sortingEnabled: false,
  },
  {
    id: 'startTime',
    numeric: false,
    disablePadding: false,
    label: 'Start',
    sortingEnabled: false,
  },
  {
    id: 'endTime',
    numeric: false,
    disablePadding: false,
    label: 'End',
    sortingEnabled: false,
  },
  {
    id: 'location',
    numeric: false,
    disablePadding: false,
    label: 'Location',
    sortingEnabled: false,
  },
  {
    id: 'facilityId',
    numeric: false,
    disablePadding: false,
    label: 'Facility Id',
    sortingEnabled: false,
  },
  {
    id: 'instructors',
    numeric: false,
    disablePadding: false,
    label: 'Instructor(s)',
    sortingEnabled: true,
  },
];

const StyledTablePagination = styled(TablePagination)({
  '& p': {
    margin: 0,
  },
});

function EnhancedTableHead(props) {
  const {
    classes,
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={false}
          >
            {headCell.sortingEnabled && (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
                className="text-nowrap"
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            )}
            {!headCell.sortingEnabled && <>{headCell.label}</>}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

// Using styled API instead of makeStyles
const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(1),
  '&.highlight': {
    color:
      theme.palette.mode === 'light'
        ? theme.palette.secondary.main
        : theme.palette.text.primary,
    backgroundColor:
      theme.palette.mode === 'light'
        ? alpha(theme.palette.secondary.light, 0.85)
        : theme.palette.secondary.dark,
  },
}));

const EnhancedTableToolbar = (props) => {
  const { numSelected } = props;

  return (
    <StyledToolbar
      className={clsx({
        highlight: numSelected > 0,
      })}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: '1 1 100%' }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{ flex: '1 1 100%' }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          Nutrition
        </Typography>
      )}

      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton aria-label="delete">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton aria-label="filter list">
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
    </StyledToolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

// Styles using sx prop and styled components
const StyledRoot = styled('div')({
  width: '100%',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(2),
}));

const visuallyHiddenStyle = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  top: 20,
  width: 1,
};

export default function EnhancedTable(props) {
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('calories');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    //console.log("set order by to:" + property + " and order = " + (isAsc ? 'desc' : 'asc'));
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      //const newSelecteds = props.rows.map((n) => n.classNumber);
      props.setSelected(props.rows);
      return;
    }
    props.setSelected([]);
  };

  const handleClick = (event, row) => {
    //const selectedIndex = props.selected.indexOf(row);
    const selectedIndex = props.selected.findIndex(
      (element) => element.classNumber === row.classNumber
    );
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(props.selected, row);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(props.selected.slice(1));
    } else if (selectedIndex === props.selected.length - 1) {
      newSelected = newSelected.concat(props.selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        props.selected.slice(0, selectedIndex),
        props.selected.slice(selectedIndex + 1)
      );
    }

    props.setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  //const isSelected = (name) => props.selected.indexOf(name) !== -1;
  const isSelected = (classNumber) =>
    props.selected.findIndex(
      (element) => element.classNumber === classNumber
    ) !== -1;

  // Create classes object to maintain compatibility with existing code
  const classes = {
    visuallyHidden: visuallyHiddenStyle,
  };

  return (
    <StyledRoot>
      <StyledPaper>
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={'small'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={props.selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={props.rows.length}
            />
            <TableBody>
              {stableSort(props.rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isItemSelected = isSelected(row.classNumber);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      hover
                      onClick={(event) => handleClick(event, row)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.classNumber}
                      selected={isItemSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          inputProps={{ 'aria-labelledby': labelId }}
                        />
                      </TableCell>
                      <TableCell>
                        {row.hasSyllabus ? (
                          <a
                            href={
                              '/syllabus/' + row.strm + '/' + row.classNumber
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FontAwesomeIcon icon={faFile} size="lg" />
                          </a>
                        ) : (
                          ''
                        )}
                      </TableCell>
                      <TableCell>{row.course}</TableCell>
                      <TableCell>{row.description}</TableCell>
                      <TableCell>{row.classNumber}</TableCell>
                      {(() => {
                        // Create arrays to hold meeting data
                        const days = [];
                        const startTimes = [];
                        const endTimes = [];
                        const facilityIds = [];
                        const instructors = [];

                        // Map through meetings once
                        row.meetings?.forEach((meeting, index) => {
                          // Days
                          if (meeting.days !== '') {
                            days.push(
                              <div key={index} className="text-nowrap">
                                {meeting.days}
                              </div>
                            );
                          } else {
                            days.push(<div key={index}>&nbsp;</div>);
                          }

                          // Start Time
                          if (meeting.startTime) {
                            startTimes.push(
                              <div key={index} className="text-nowrap">
                                {formatTime(meeting.startTime)}
                              </div>
                            );
                          } else {
                            startTimes.push(<div key={index}>&nbsp;</div>);
                          }

                          // End Time
                          if (meeting.endTime) {
                            endTimes.push(
                              <div key={index} className="text-nowrap">
                                {formatTime(meeting.endTime)}
                              </div>
                            );
                          } else {
                            endTimes.push(<div key={index}>&nbsp;</div>);
                          }

                          // Facility ID
                          facilityIds.push(
                            <div key={index} className="text-nowrap">
                              {meeting.facilityId.includes('ASUSYNC')
                                ? 'ASU Sync'
                                : meeting.facilityId}
                            </div>
                          );

                          // Instructors
                          if (
                            meeting.instructors &&
                            meeting.instructors.length > 0
                          ) {
                            meeting.instructors.map(
                              (instructor, instIndex) =>
                                !instructors.includes(
                                  instructor.firstName +
                                    ' ' +
                                    instructor.lastName
                                ) &&
                                instructors.push(
                                  instructor.firstName +
                                    ' ' +
                                    instructor.lastName
                                )
                            );
                          }
                        });

                        // Return the TableCells with the prepared elements
                        return (
                          <>
                            <TableCell>{days}</TableCell>
                            <TableCell>{startTimes}</TableCell>
                            <TableCell>{endTimes}</TableCell>
                            <TableCell>
                              {row.locationDescription === 'Dwnwn Phx'
                                ? 'Dtphx'
                                : row.locationDescription === 'Polytech'
                                  ? 'Poly'
                                  : row.locationDescription}
                            </TableCell>
                            <TableCell>{facilityIds}</TableCell>
                            <TableCell>
                              {instructors.map((instructor, index, array) => (
                                <div key={index}>
                                  {instructor}
                                  {index < array.length - 1 ? ', ' : ''}
                                </div>
                              ))}
                            </TableCell>
                          </>
                        );
                      })()}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <StyledTablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={props.rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </StyledPaper>
    </StyledRoot>
  );
}
